// src/features/links/PlatformIcon.tsx
// IR-1047 Phase 3.5 (Rotem QA: "platform type - use icon"). Renders the
// platform as a recognizable brand glyph instead of a text label. lucide-react
// ships no Telegram/WhatsApp brand marks, so these are small inline SVGs.
// Colors match the established scraper-panel convention (tg=sky, wa=green,
// signal=purple). The full platform name is kept as an sr-only label + title
// so the cell stays accessible and screen-reader/test-queryable.
import { PLATFORM_LABELS } from "./labels";
import type { Platform } from "./types";

const PLATFORM_COLOR: Record<Platform, string> = {
  tg: "rgb(56, 189, 248)", // sky
  wa: "rgb(34, 197, 94)", // green
  signal: "rgb(168, 85, 247)", // purple
};

// Brand glyph paths (24x24 viewBox, fill=currentColor).
const PLATFORM_PATH: Record<Platform, string> = {
  // Telegram paper-plane.
  tg: "M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z",
  // WhatsApp phone-in-bubble.
  wa: "M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0012.04 2zm5.8 14.16c-.24.68-1.4 1.3-1.93 1.38-.49.07-1.13.1-1.82-.11-.42-.13-.96-.31-1.65-.61-2.9-1.25-4.8-4.17-4.94-4.36-.15-.19-1.18-1.57-1.18-3 0-1.42.75-2.12 1.01-2.41.26-.29.57-.36.76-.36l.54.01c.17.01.41-.07.64.49.24.59.82 2.01.89 2.16.07.15.12.32.02.51-.09.19-.14.31-.28.48-.14.17-.3.37-.43.5-.14.14-.29.29-.12.57.17.29.74 1.22 1.59 1.98 1.09.97 2.01 1.27 2.3 1.42.29.14.46.12.63-.07.17-.19.73-.85.92-1.14.19-.29.39-.24.65-.14.26.09 1.67.79 1.96.93.29.14.48.22.55.34.07.12.07.7-.17 1.38z",
  // Signal chat-bubble.
  signal:
    "M12 2C6.48 2 2 5.94 2 10.8c0 2.4 1.1 4.57 2.88 6.13-.13 1.07-.6 2.5-1.42 3.57-.13.17 0 .4.2.36 1.7-.34 3.1-.96 4.05-1.5C9.04 19.78 10.48 20 12 20c5.52 0 10-3.94 10-8.8C22 5.94 17.52 2 12 2z",
};

// IR-1147 reopen: map an entity/avatar app_type (e.g. "telegram") to the
// platform code the brand glyphs use ("tg"/"wa"/"signal"). Lives here rather
// than in groups-unified-tab (its original home) because IR-1479 r3 made the
// Avatars table render the same glyph, and avatars-tab must not import from
// groups-unified-tab — that file already imports FROM avatars-tab, so the
// reverse edge would be a circular import.
const PLATFORM_ICON_CODE: Record<string, Platform> = {
  telegram: "tg",
  whatsapp: "wa",
  signal: "signal",
};
export function platformToIconCode(platform: string | null | undefined): Platform | null {
  if (!platform) return null;
  return PLATFORM_ICON_CODE[platform.toLowerCase()] ?? null;
}

interface Props {
  platform: Platform;
}

export function PlatformIcon({ platform }: Props) {
  const label = PLATFORM_LABELS[platform] ?? platform;
  const path = PLATFORM_PATH[platform];

  if (!path) {
    // Unknown platform code — fall back to the text label.
    return <span className="text-xs">{label}</span>;
  }

  return (
    <span className="inline-flex items-center" title={label}>
      <svg
        viewBox="0 0 24 24"
        width={16}
        height={16}
        fill={PLATFORM_COLOR[platform]}
        aria-hidden="true"
        role="img"
      >
        <path d={path} />
      </svg>
      <span className="sr-only">{label}</span>
    </span>
  );
}
